import type { LayoutEntry, PaidModule, WidgetSize } from '@palaccount/contracts';

/**
 * DSH-00 — the ONE shared board engine (§14.1.7): "All three dashboards run
 * on one shared board engine — the same drag, resize, hide, add, reset and
 * save code, with a different widget list per board."
 *
 * Pure functions over the spec's own record, `[{id, size, visible, order}]`
 * (R1: exactly one per user per board). The React board calls these and
 * persists the result; nothing here touches the network or the DOM.
 */

/** One widget definition — a row of the §5c catalogue tables. */
export interface WidgetDef {
  id: string;
  /** i18n key for the title. */
  titleKey: string;
  /** `kpi` | `chart` | `table` — the §14.1.4 category label. */
  category: 'kpi' | 'chart' | 'table';
  /** §14.1.6: the sizes this widget allows. KPI cards allow ['sm'] alone. */
  sizes: readonly WidgetSize[];
  /** "On the board by default" of §5c. */
  defaultVisible: boolean;
  /** The default size when visible. */
  defaultSize: WidgetSize;
  /**
   * BR-DSH-00-17's gate, resolved by the caller against /api/me:
   * the permission areas whose data this widget shows. A widget is offered
   * only when the viewer holds every listed area's view.
   */
  needs?: readonly string[];
  /**
   * BR-DSH-00-18's gate, the same shape one level up: the PAID MODULE the
   * company must hold, resolved against /api/me's `modules`. Absent on almost
   * every widget, which is the point — a widget with no `module` is offered to
   * every company, exactly as before.
   *
   * It is a second question rather than another entry in `needs` because the
   * two answer differently and §1.5.2 fixes the order: permission is checked
   * first, then the module. A module is a fact about the COMPANY that no role
   * overrides — "a switched-off module is off for the whole company and for
   * every user in it, whatever their role" (§1.5.3) — so the Owner flag, which
   * short-circuits every permission, does not short-circuit this one. Same
   * distinction `NavGroup.module` already draws for menu groups.
   */
  module?: PaidModule;
}

/** BR-DSH-00-09: the delivered default layout for a catalogue. */
export function defaultLayout(catalog: readonly WidgetDef[]): LayoutEntry[] {
  return catalog
    .filter((w) => w.defaultVisible)
    .map((w, index) => ({ id: w.id, size: w.defaultSize, visible: true, order: index }));
}

/**
 * Merge a saved layout with the catalogue. Unknown ids are dropped silently
 * (DSH-02 §15: "a widget in the saved layout was removed from the product…
 * drop it and render the rest"); an empty saved layout means the delivered
 * default. Catalogue widgets absent from the saved record stay hidden (they
 * were hidden when the record was saved, or arrived later).
 */
export function resolveLayout(
  catalog: readonly WidgetDef[],
  saved: readonly LayoutEntry[],
): LayoutEntry[] {
  if (saved.length === 0) return defaultLayout(catalog);
  const known = new Set(catalog.map((w) => w.id));
  return [...saved]
    .filter((entry) => known.has(entry.id))
    .sort((a, b) => a.order - b.order)
    .map((entry, index) => ({ ...entry, order: index }));
}

/** The widgets drawn on the board, in order. */
export function visibleWidgets(layout: readonly LayoutEntry[]): LayoutEntry[] {
  return layout.filter((e) => e.visible).sort((a, b) => a.order - b.order);
}

/**
 * §14.1.4: the panel offers the full set minus whatever is visible —
 * "the catalogue is computed as the full widget set minus whatever is
 * currently visible".
 */
export function catalogTiles(
  catalog: readonly WidgetDef[],
  layout: readonly LayoutEntry[],
): WidgetDef[] {
  const visible = new Set(visibleWidgets(layout).map((e) => e.id));
  return catalog.filter((w) => !visible.has(w.id));
}

/**
 * BR-DSH-00-23: dragging A onto B moves A into B's position and shifts the
 * others along. Never a free grid.
 */
export function moveWidget(
  layout: readonly LayoutEntry[],
  fromId: string,
  toId: string,
): LayoutEntry[] {
  if (fromId === toId) return [...layout];
  const ordered = [...layout].sort((a, b) => a.order - b.order);
  const fromIndex = ordered.findIndex((e) => e.id === fromId);
  const toIndex = ordered.findIndex((e) => e.id === toId);
  if (fromIndex < 0 || toIndex < 0) return [...layout];
  const [moved] = ordered.splice(fromIndex, 1);
  ordered.splice(toIndex, 0, moved!);
  return ordered.map((e, index) => ({ ...e, order: index }));
}

/**
 * §14.1.5/§14.1.6: the resize button steps to the NEXT allowed size and
 * wraps. A widget with one allowed size never changes (and must not offer
 * the button — BR-DSH-00-11; the UI half).
 */
export function cycleSize(
  layout: readonly LayoutEntry[],
  id: string,
  allowed: readonly WidgetSize[],
): LayoutEntry[] {
  if (allowed.length <= 1) return [...layout];
  return layout.map((e) => {
    if (e.id !== id) return e;
    const at = allowed.indexOf(e.size);
    const next = allowed[(at + 1) % allowed.length]!;
    return { ...e, size: next };
  });
}

/** BR-DSH-00-05: hide removes from the board; the tile reappears in the panel. */
export function hideWidget(layout: readonly LayoutEntry[], id: string): LayoutEntry[] {
  return layout.map((e) => (e.id === id ? { ...e, visible: false } : e));
}

/** BR-DSH-00-06: a tile adds its widget to the END of the grid. */
export function addWidget(
  layout: readonly LayoutEntry[],
  widget: WidgetDef,
): LayoutEntry[] {
  const maxOrder = layout.reduce((max, e) => Math.max(max, e.order), -1);
  const existing = layout.find((e) => e.id === widget.id);
  if (existing) {
    return layout.map((e) =>
      e.id === widget.id ? { ...e, visible: true, order: maxOrder + 1 } : e,
    );
  }
  return [
    ...layout,
    { id: widget.id, size: widget.defaultSize, visible: true, order: maxOrder + 1 },
  ];
}

/** BR-DSH-00-07, BR-DSH-00-08 helpers. */
export function boardIsEmpty(layout: readonly LayoutEntry[]): boolean {
  return visibleWidgets(layout).length === 0;
}
export function allAdded(catalog: readonly WidgetDef[], layout: readonly LayoutEntry[]): boolean {
  return catalogTiles(catalog, layout).length === 0;
}
